/**
 * Printable payment documents.
 *
 * Rendered as a self-contained HTML document and handed to the browser's
 * print dialog, where the user saves as PDF. No PDF dependency, and because
 * the brand colours are read from the live kx tokens the output follows a
 * whitelabel build automatically.
 */
import moment from 'moment';
import { brandName, OPERATING_ENTITY } from './brand';

/* fields are widened to allow null because the payment records carry nulls
   for anything the provider did not return */
export interface IReceiptPayment {
  id?: number | string | null;
  /** issued server-side when the payment settles; absent on older records */
  receiptNumber?: string | null;
  uuid?: string | null;
  createdAt?: string | null;
  transactionId?: string | number | null;
  amount?: number | null;
  channel?: string | null;
  serviceProvider?: string | null;
  status?: string | null;
  email?: string | null;
  reason?: string | null;
}

/** read a kx token off the root element so whitelabel builds carry through */
const token = (name: string, fallback: string): string => {
  if (typeof document === 'undefined') {
    return fallback;
  }
  const value = getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
  return value || fallback;
};

const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** documents are printed, so any interpolated value must be escaped */
const escapeHtml = (value: unknown): string =>
  `${value ?? ''}`.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] ?? char);

const money = (amount?: number | null): string =>
  `GH₵ ${Number(amount ?? 0).toLocaleString('en-GH', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const date = (value?: string | null, format = 'DD MMM YYYY, hh:mm a'): string =>
  value ? moment(value).format(format) : '—';

/**
 * The issued receipt number, which is what the document should show.
 *
 * Payments that settled before receipt numbering existed have none, and
 * payments that never settled never get one. Those fall back to quoting the
 * transaction reference in full, labelled as such, rather than inventing a
 * number that no register would recognise.
 */
const receiptReference = (
  payment: IReceiptPayment
): { label: string; value: string } => {
  if (payment.receiptNumber) {
    return { label: 'Receipt No.', value: payment.receiptNumber };
  }
  return {
    label: 'Transaction reference',
    value: `${payment.transactionId ?? payment.uuid ?? payment.id ?? '—'}`,
  };
};

const documentShell = (title: string, body: string): string => {
  const pink = token('--kx-pink', '#ff0055');
  const navy = token('--kx-navy', '#14294f');
  const brand = escapeHtml(brandName());
  const operatedBy =
    brandName() === OPERATING_ENTITY
      ? ''
      : `<p class="operated">Operated by ${escapeHtml(OPERATING_ENTITY)}</p>`;
  return `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  * { box-sizing: border-box; }
  body {
    margin: 0; padding: 40px;
    font-family: 'Hanken Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
    color: #14181c; font-size: 13px; line-height: 1.55;
    -webkit-print-color-adjust: exact; print-color-adjust: exact;
  }
  .head { display: flex; justify-content: space-between; align-items: flex-start;
    border-bottom: 3px solid ${pink}; padding-bottom: 18px; margin-bottom: 26px; }
  .brand { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; color: ${navy}; }
  .operated { margin: 2px 0 0; font-size: 11px; color: #5c6673; }
  .doc-type { text-align: right; }
  .doc-type h1 { margin: 0; font-size: 15px; letter-spacing: 0.14em;
    text-transform: uppercase; color: ${pink}; }
  .doc-type .ref { font-family: ui-monospace, 'SF Mono', Menlo, monospace;
    font-size: 12px; color: #2e3844; margin-top: 4px; }
  .grid { display: flex; flex-wrap: wrap; gap: 22px 48px; margin-bottom: 26px; }
  .field span { display: block; font-size: 9.5px; letter-spacing: 0.12em;
    text-transform: uppercase; color: #667080; margin-bottom: 3px; }
  .field strong { font-size: 13.5px; font-weight: 600; color: #14181c; }
  .total { background: #f6f7f9; border: 1px solid rgba(20,24,28,.1);
    border-radius: 12px; padding: 18px 22px; display: flex;
    justify-content: space-between; align-items: center; margin-bottom: 26px; }
  .total span { font-size: 10px; letter-spacing: 0.12em; text-transform: uppercase; color: #667080; }
  .total strong { font-size: 24px; font-weight: 700; color: ${navy}; letter-spacing: -0.01em; }
  table { width: 100%; border-collapse: collapse; margin-bottom: 22px; }
  th { text-align: left; font-size: 9.5px; letter-spacing: 0.1em; text-transform: uppercase;
    color: #414b58; border-bottom: 1px solid rgba(20,24,28,.14); padding: 8px 8px 8px 0; }
  td { padding: 9px 8px 9px 0; border-bottom: 1px solid rgba(20,24,28,.07); font-size: 12.5px; }
  td.num, th.num { text-align: right; padding-right: 0; }
  .pill { display: inline-block; padding: 2px 9px; border-radius: 999px;
    font-size: 10px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; }
  .ok { background: #d3eedd; color: #0a4b28; }
  .bad { background: #fbdcd8; color: #96170b; }
  .other { background: #eef0f3; color: #2e3844; }
  .foot { margin-top: 34px; padding-top: 16px; border-top: 1px solid rgba(20,24,28,.1);
    font-size: 11px; color: #5c6673; display: flex; justify-content: space-between; gap: 20px; }
  @page { margin: 14mm; }
  @media print { body { padding: 0; } }
</style>
</head>
<body>
  <div class="head">
    <div>
      <div class="brand">${brand}</div>
      ${operatedBy}
    </div>
    <div class="doc-type">
      <h1>${escapeHtml(title)}</h1>
    </div>
  </div>
  ${body}
  <div class="foot">
    <span>Generated ${date(new Date().toISOString())}</span>
    <span>This document is computer generated and needs no signature.</span>
  </div>
</body>
</html>`;
};

const statusPill = (status?: string | null): string => {
  const value = `${status ?? 'N/A'}`.toUpperCase();
  const cls = ['SUCCESS', 'SUCCESSFUL', 'COMPLETED', 'PAID'].includes(value)
    ? 'ok'
    : ['FAILED', 'CANCELLED', 'DECLINED'].includes(value)
    ? 'bad'
    : 'other';
  return `<span class="pill ${cls}">${escapeHtml(value)}</span>`;
};

/**
 * Hand a document to the browser to print. Opened via a blob URL in a hidden
 * frame so it prints without a popup window and without navigating away.
 */
const printDocument = (html: string): boolean => {
  const frame = document.createElement('iframe');
  frame.style.cssText =
    'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(frame);
  const doc = frame.contentWindow?.document;
  if (!doc) {
    frame.remove();
    return false;
  }
  doc.open();
  doc.write(html);
  doc.close();
  const run = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    /* leave the frame long enough for the dialog to read it */
    setTimeout(() => frame.remove(), 60000);
  };
  if (frame.contentWindow?.document.readyState === 'complete') {
    setTimeout(run, 250);
  } else {
    frame.onload = () => setTimeout(run, 250);
  }
  return true;
};

/** Receipt for a single payment. */
export const printPaymentReceipt = (payment: IReceiptPayment): boolean => {
  const body = `
  <div class="doc-type" style="text-align:left;margin:-14px 0 22px">
    <div class="ref">${escapeHtml(
      `${receiptReference(payment).label}: ${receiptReference(payment).value}`
    )}</div>
  </div>
  <div class="grid">
    <div class="field"><span>Date paid</span><strong>${escapeHtml(
      date(payment.createdAt)
    )}</strong></div>
    <div class="field"><span>Paid by</span><strong>${escapeHtml(
      payment.email || '—'
    )}</strong></div>
    <div class="field"><span>Status</span><strong>${statusPill(
      payment.status
    )}</strong></div>
  </div>
  <div class="total">
    <span>Amount paid</span>
    <strong>${escapeHtml(money(payment.amount))}</strong>
  </div>
  <div class="grid">
    <div class="field"><span>Transaction ID</span><strong>${escapeHtml(
      payment.transactionId || '—'
    )}</strong></div>
    <div class="field"><span>Channel</span><strong>${escapeHtml(
      payment.channel || '—'
    )}</strong></div>
    <div class="field"><span>Provider</span><strong>${escapeHtml(
      payment.serviceProvider || '—'
    )}</strong></div>
  </div>
  ${
    payment.reason
      ? `<div class="field"><span>Note</span><strong>${escapeHtml(
          payment.reason
        )}</strong></div>`
      : ''
  }`;
  return printDocument(documentShell('Payment Receipt', body));
};

/** Statement covering the payments currently in view. */
export const printPaymentStatement = (
  payments: IReceiptPayment[],
  range?: { from?: string; to?: string }
): boolean => {
  const settled = payments.filter((payment) =>
    ['SUCCESS', 'SUCCESSFUL', 'COMPLETED', 'PAID'].includes(
      `${payment.status ?? ''}`.toUpperCase()
    )
  );
  const total = settled.reduce(
    (sum, payment) => sum + Number(payment.amount ?? 0),
    0
  );
  const rows = payments
    .map(
      (payment) => `<tr>
      <td>${escapeHtml(date(payment.createdAt, 'DD MMM YYYY'))}</td>
      <td>${escapeHtml(payment.transactionId || '—')}</td>
      <td>${escapeHtml(payment.channel || '—')}</td>
      <td>${statusPill(payment.status)}</td>
      <td class="num">${escapeHtml(money(payment.amount))}</td>
    </tr>`
    )
    .join('');
  const period =
    range?.from || range?.to
      ? `${date(range?.from, 'DD MMM YYYY')} — ${date(
          range?.to,
          'DD MMM YYYY'
        )}`
      : 'All payments';
  const body = `
  <div class="grid">
    <div class="field"><span>Period</span><strong>${escapeHtml(
      period
    )}</strong></div>
    <div class="field"><span>Payments listed</span><strong>${
      payments.length
    }</strong></div>
    <div class="field"><span>Settled</span><strong>${
      settled.length
    }</strong></div>
  </div>
  <div class="total">
    <span>Total settled</span>
    <strong>${escapeHtml(money(total))}</strong>
  </div>
  <table>
    <thead><tr>
      <th>Date</th><th>Transaction ID</th><th>Channel</th><th>Status</th><th class="num">Amount</th>
    </tr></thead>
    <tbody>${
      rows || '<tr><td colspan="5">No payments in this period.</td></tr>'
    }</tbody>
  </table>
  <p style="font-size:11px;color:#5c6673;margin:0">
    The total covers settled payments only; pending and failed entries are listed but excluded.
  </p>`;
  return printDocument(documentShell('Payment Statement', body));
};

export interface IPrintableInvoice {
  invoiceNumber?: string | null;
  status?: string | null;
  amount?: number | string | null;
  credits?: number | null;
  unitPrice?: number | string | null;
  currency?: string | null;
  description?: string | null;
  recipientMsisdn?: string | null;
  issuedAt?: string | null;
  dueAt?: string | null;
  paidAt?: string | null;
}

/**
 * The invoice document. Shares the receipt's shell, so both carry the same
 * brand treatment and follow a whitelabel build.
 */
export const printInvoice = (invoice: IPrintableInvoice): boolean => {
  const isPaid = `${invoice.status ?? ''}`.toUpperCase() === 'PAID';
  const body = `
  <div class="doc-type" style="text-align:left;margin:-14px 0 22px">
    <div class="ref">${escapeHtml(invoice.invoiceNumber || '—')}</div>
  </div>
  <div class="grid">
    <div class="field"><span>Issued</span><strong>${escapeHtml(
      date(invoice.issuedAt, 'DD MMM YYYY')
    )}</strong></div>
    <div class="field"><span>${
      isPaid ? 'Paid' : 'Due by'
    }</span><strong>${escapeHtml(
    date(isPaid ? invoice.paidAt : invoice.dueAt, 'DD MMM YYYY')
  )}</strong></div>
    <div class="field"><span>Status</span><strong>${statusPill(
      invoice.status
    )}</strong></div>
  </div>
  <table>
    <thead><tr>
      <th>Description</th><th class="num">Units</th><th class="num">Unit price</th><th class="num">Amount</th>
    </tr></thead>
    <tbody><tr>
      <td>${escapeHtml(invoice.description || 'SMS credits')}</td>
      <td class="num">${Number(invoice.credits ?? 0).toLocaleString()}</td>
      <td class="num">${escapeHtml(
        `GH₵ ${Number(invoice.unitPrice ?? 0).toFixed(4)}`
      )}</td>
      <td class="num">${escapeHtml(money(Number(invoice.amount ?? 0)))}</td>
    </tr></tbody>
  </table>
  <div class="total">
    <span>${isPaid ? 'Total paid' : 'Total due'}</span>
    <strong>${escapeHtml(money(Number(invoice.amount ?? 0)))}</strong>
  </div>
  ${
    invoice.recipientMsisdn
      ? `<div class="field"><span>Payable from</span><strong>${escapeHtml(
          invoice.recipientMsisdn
        )}</strong></div>`
      : ''
  }
  ${
    isPaid
      ? ''
      : `<p style="font-size:11px;color:#5c6673;margin:18px 0 0">
           This invoice is payable until ${escapeHtml(
             date(invoice.dueAt, 'DD MMM YYYY')
           )}. After that date a new invoice must be raised.
         </p>`
  }`;
  return printDocument(documentShell('Invoice', body));
};
